import { Extension } from '@tiptap/core';
import { Plugin, PluginKey } from '@tiptap/pm/state';
import { DOMSerializer, Fragment, Slice } from '@tiptap/pm/model';
import { sanitizeContentClipboardNode } from './contentEditorCommands.js';

export const CONTENT_CLIPBOARD_TYPE = 'application/x-mts-content-editor+json';

function cleanSlice(slice, schema) {
  const nodes = [];
  slice.content.forEach((node) => nodes.push(schema.nodeFromJSON(sanitizeContentClipboardNode(node.toJSON()))));
  return new Slice(Fragment.fromArray(nodes), slice.openStart, slice.openEnd);
}

/** Native keyboard and context-menu clipboard operations share one neutral transport. */
export function createContentEditorClipboard(getContext) {
  return Extension.create({
    name: 'contentEditorClipboard',
    addProseMirrorPlugins() {
      const copy = (view, event, cut) => {
        if (!event.clipboardData || view.state.selection.empty) return false;
        const slice = cleanSlice(view.state.selection.content(), view.state.schema);
        const context = getContext();
        const container = document.createElement('div');
        container.appendChild(DOMSerializer.fromSchema(view.state.schema).serializeFragment(slice.content));
        event.clipboardData.setData(CONTENT_CLIPBOARD_TYPE, JSON.stringify({ schemaVersion: 1, tenantId: context.tenantId || null, slice: { ...slice.toJSON(), content: slice.content.toJSON().map(sanitizeContentClipboardNode) } }));
        event.clipboardData.setData('text/html', container.innerHTML);
        event.clipboardData.setData('text/plain', slice.content.textBetween(0, slice.content.size, '\n\n'));
        event.preventDefault();
        if (cut && this.editor.isEditable) view.dispatch(view.state.tr.deleteSelection().scrollIntoView());
        return true;
      };
      return [new Plugin({ key: new PluginKey('mtsContentEditorClipboard'), props: {
        transformPasted: (slice) => cleanSlice(slice, this.editor.schema),
        transformCopied: (slice) => cleanSlice(slice, this.editor.schema),
        handleDOMEvents: { copy: (view, event) => copy(view, event, false), cut: (view, event) => copy(view, event, true) },
        handlePaste: (view, event) => {
          const raw = event.clipboardData?.getData(CONTENT_CLIPBOARD_TYPE);
          if (!raw) return false;
          event.preventDefault();
          const context = getContext();
          try {
            const value = JSON.parse(raw);
            if (value.schemaVersion !== 1 || value.tenantId !== (context.tenantId || null)) throw new Error('Clipboard content belongs to a different editor context.');
            if (!this.editor.isEditable) throw new Error('This document is read-only.');
            const visit = (node) => {
              if (!view.state.schema.nodes[node.type]) throw new Error(`Unavailable clipboard node: ${node.type}.`);
              for (const mark of node.marks || []) if (!view.state.schema.marks[mark.type]) throw new Error(`Unavailable clipboard mark: ${mark.type}.`);
              for (const child of node.content || []) visit(child);
            };
            for (const node of value.slice?.content || []) visit(node);
            const slice = cleanSlice(Slice.fromJSON(view.state.schema, value.slice), view.state.schema);
            const original = view.state.doc;
            view.dispatch(view.state.tr.replaceSelection(slice).scrollIntoView());
            if (view.state.doc === original) throw new Error('This clipboard content could not be inserted.');
          } catch (error) { context.onRejected?.([{ code: 'clipboard-rejected', severity: 'error', message: error.message }]); }
          return true;
        },
      } })];
    },
  });
}
